import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.js';
import { preguntarAsistente } from '../../services/asistenteService.js';

const MENSAJE_BIENVENIDA = {
  role: 'assistant',
  content:
    '¡Hola! Soy el asistente de la app Sentry. Pregúntame cómo usar ' +
    'cualquier función: fichar, hacer un reporte, rondas, cuadrantes, ' +
    'cambios de turno, el Portal del Empleado, etc.',
};

// Nº máximo de turnos previos que enviamos como contexto al backend.
const MAX_HISTORIAL = 10;

const AsistenteIA = () => {
  const { user } = useAuth();
  const [abierto, setAbierto] = useState(false);
  const [mensajes, setMensajes] = useState([MENSAJE_BIENVENIDA]);
  const [texto, setTexto] = useState('');
  const [cargando, setCargando] = useState(false);
  const finRef = useRef(null);

  useEffect(() => {
    if (finRef.current) {
      finRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [mensajes, abierto]);

  // Solo se muestra a usuarios autenticados.
  if (!user) return null;

  const enviar = async () => {
    const pregunta = texto.trim();
    if (!pregunta || cargando) return;

    const historial = mensajes
      .filter((m) => m !== MENSAJE_BIENVENIDA)
      .slice(-MAX_HISTORIAL)
      .map((m) => ({ role: m.role, content: m.content }));

    setMensajes((prev) => [...prev, { role: 'user', content: pregunta }]);
    setTexto('');
    setCargando(true);

    try {
      const respuesta = await preguntarAsistente(pregunta, historial);
      setMensajes((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            respuesta ||
            'No he podido generar una respuesta. Inténtalo de nuevo.',
        },
      ]);
    } catch (err) {
      setMensajes((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            'Lo siento, ha ocurrido un error al contactar con el ' +
            'asistente. Inténtalo de nuevo en un momento.',
        },
      ]);
    } finally {
      setCargando(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  return (
    <>
      {/* Botón flotante */}
      {!abierto && (
        <button
          onClick={() => setAbierto(true)}
          aria-label="Abrir asistente de ayuda"
          className="fixed bottom-5 right-5 z-50 bg-[#4677c2] text-white
            rounded-full w-14 h-14 flex items-center justify-center shadow-lg
            hover:bg-[#3a66ab] transition-colors"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor"
            viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9
                8a9.86 9.86 0 01-4-.8L3 21l1.9-3.8A7.96 7.96 0 013
                12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      )}

      {/* Panel de chat */}
      {abierto && (
        <div
          className="fixed bottom-5 right-5 z-50 flex flex-col bg-white
            rounded-xl shadow-2xl border border-gray-200
            w-[90vw] max-w-sm h-[70vh] max-h-[600px]"
        >
          {/* Cabecera */}
          <div className="bg-[#4677c2] text-white px-4 py-3 rounded-t-xl
            flex items-center justify-between">
            <span className="font-medium">Asistente de ayuda</span>
            <button
              onClick={() => setAbierto(false)}
              aria-label="Cerrar asistente"
              className="hover:bg-white/20 rounded-full p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round"
                  strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mensajes */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
            {mensajes.map((m, i) => (
              <div
                key={i}
                className={
                  m.role === 'user' ? 'flex justify-end' : 'flex justify-start'
                }
              >
                <div
                  className={
                    'max-w-[85%] rounded-2xl px-3 py-2 text-sm ' +
                    'whitespace-pre-wrap ' +
                    (m.role === 'user'
                      ? 'bg-[#4677c2] text-white rounded-br-sm'
                      : 'bg-white text-gray-800 border border-gray-200 ' +
                        'rounded-bl-sm')
                  }
                >
                  {m.content}
                </div>
              </div>
            ))}
            {cargando && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-500 border border-gray-200
                  rounded-2xl rounded-bl-sm px-3 py-2 text-sm">
                  Escribiendo…
                </div>
              </div>
            )}
            <div ref={finRef} />
          </div>

          {/* Entrada */}
          <div className="p-2 border-t border-gray-200 flex items-end gap-2">
            <textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Escribe tu duda…"
              className="flex-1 resize-none border border-gray-300 rounded-lg
                px-3 py-2 text-sm focus:outline-none focus:ring-2
                focus:ring-[#4677c2] max-h-24"
            />
            <button
              onClick={enviar}
              disabled={cargando || !texto.trim()}
              className="bg-[#4677c2] text-white rounded-lg px-3 py-2
                disabled:opacity-50 hover:bg-[#3a66ab] transition-colors"
              aria-label="Enviar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round"
                  strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AsistenteIA;
